import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useNodes } from '@/lib/cluster'
import { useNodeMetrics, type MetricRange } from '@/lib/metrics'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MetricLine } from '@/components/charts/MetricLine'
import { GpuPanel } from '@/components/GpuPanel'
import { NodeContainerTop } from '@/components/NodeContainerTop'
import { bytes, bps, pct } from '@/lib/format'

const RANGES: MetricRange[] = ['1h', '6h', '24h', '7d', '30d']

export function NodeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: nodes } = useNodes()
  const node = nodes?.find((n) => n.id === id)

  const [range, setRange] = useState<MetricRange>('1h')
  const { data, isLoading } = useNodeMetrics(id, range)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/nodes" className="text-sm text-muted-foreground hover:underline">
            ← Back to nodes
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">{node?.name ?? id}</h1>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <Button
              key={r}
              variant={r === range ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setRange(r)}
            >
              {r}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <Skeleton className="h-52" />}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">CPU %</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricLine
                data={data.points as unknown as Record<string, unknown>[]}
                xKey="b"
                yKey="cpu_pct"
                format={(v) => pct(v ?? null, 1)}
                label="CPU %"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Memory used</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricLine
                data={data.points as unknown as Record<string, unknown>[]}
                xKey="b"
                yKey="mem_used"
                format={(v) => bytes(v ?? null)}
                label="Memory"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Network in</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricLine
                data={data.points as unknown as Record<string, unknown>[]}
                xKey="b"
                yKey="net_rx_bps"
                format={(v) => bps(v ?? null)}
                label="Net in"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Network out</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricLine
                data={data.points as unknown as Record<string, unknown>[]}
                xKey="b"
                yKey="net_tx_bps"
                format={(v) => bps(v ?? null)}
                label="Net out"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {id && <NodeContainerTop nodeId={id} range={range} />}

      {node?.gpus && node.gpus.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">GPUs</h2>
          <div className="space-y-4">
            {node.gpus.map((g) => (
              <GpuPanel key={g.gpu_uuid} nodeId={node.id} gpu={g} range={range} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
